import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, Save, Sparkles, Target, Megaphone, Layers, Map, Trash2, Plus, AlertTriangle } from 'lucide-react';
import { tenantSettingsApi, TenantSettings, ContentStrategy } from '../../../services/tenant-settings-api';

import { useAssistantAction } from '@/contexts/AssistantActionContext';
import { useAuth } from '@/context/AuthContext';
import { ContentStrategyAIChat } from './ContentStrategyAIChat';
import { FormInput, FormTextArea, Label } from '@/components/common/form';
import { Button } from '@/components/common/Button';

const ContentStrategyPage: React.FC = () => {
    const { user } = useAuth();
    const isReadOnly = user?.tenant?.role !== 'owner' && user?.tenant?.role !== 'admin';
    const [chatSection, setChatSection] = useState<string | null>(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState<TenantSettings>({});
    const [strategy, setStrategy] = useState<ContentStrategy>({
        targetAudience: { persona: '', painPoints: [], goal: '' },
        brandVoice: { tone: '', style: '', traits: [] },
        contentPillars: [],
        journeyStages: []
    });

    const { triggerAssistant } = useAssistantAction();

    // Load settings on mount
    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async (background: boolean = false) => {
        try {
            if (!background) setLoading(true);
            const data = await tenantSettingsApi.getSettings();
            setSettings(data);
            if (data.contentStrategy) {
                // Merge with default structure to ensure arrays exist
                setStrategy({
                    targetAudience: {
                        persona: data.contentStrategy.targetAudience?.persona || '',
                        painPoints: data.contentStrategy.targetAudience?.painPoints || [],
                        goal: data.contentStrategy.targetAudience?.goal || ''
                    },
                    brandVoice: {
                        tone: data.contentStrategy.brandVoice?.tone || '',
                        style: data.contentStrategy.brandVoice?.style || '',
                        traits: data.contentStrategy.brandVoice?.traits || []
                    },
                    contentPillars: data.contentStrategy.contentPillars || [],
                    journeyStages: data.contentStrategy.journeyStages || []
                });
            }
        } catch (error) {
            console.error('Failed to load settings:', error);
            toast.error('Failed to load content strategy settings');
        } finally {
            if (!background) setLoading(false);
        }
    };

    const handleSave = async (strategyToSave?: ContentStrategy, silent: boolean = false) => {
        try {
            setSaving(true);
            const currentStrategy = strategyToSave || strategy;
            const updatedSettings: TenantSettings = {
                ...settings,
                contentStrategy: currentStrategy
            };
            await tenantSettingsApi.updateSettings(updatedSettings);
            setSettings(updatedSettings);
            if (!silent) {
                toast.success('Content strategy saved');
            }
        } catch (error) {
            console.error('Failed to save settings:', error);
            if (!silent) {
                toast.error('Failed to save content strategy');
            }
        } finally {
            setSaving(false);
        }
    };

    // ... (helper functions will be updated in next step) ...

    // ...

    // In render:
    <div className="flex gap-2">
        {/* Save button removed for auto-save */}
    </div>

    // --- Helper Functions for Array Fields ---

    const addPainPoint = () => {
        setStrategy(prev => ({
            ...prev,
            targetAudience: {
                ...prev.targetAudience!,
                painPoints: [...(prev.targetAudience?.painPoints || []), '']
            }
        }));
    };

    const updatePainPoint = (index: number, value: string) => {
        const newPoints = [...(strategy.targetAudience?.painPoints || [])];
        newPoints[index] = value;
        setStrategy(prev => ({
            ...prev,
            targetAudience: { ...prev.targetAudience!, painPoints: newPoints }
        }));
    };

    const removePainPoint = (index: number) => {
        const newPoints = [...(strategy.targetAudience?.painPoints || [])];
        newPoints.splice(index, 1);
        const newStrategy = {
            ...strategy,
            targetAudience: { ...strategy.targetAudience!, painPoints: newPoints }
        };
        setStrategy(newStrategy);
        handleSave(newStrategy, true);
    };

    const addTrait = () => {
        setStrategy(prev => ({
            ...prev,
            brandVoice: {
                ...prev.brandVoice!,
                traits: [...(prev.brandVoice?.traits || []), '']
            }
        }));
    };

    const updateTrait = (index: number, value: string) => {
        const newTraits = [...(strategy.brandVoice?.traits || [])];
        newTraits[index] = value;
        setStrategy(prev => ({
            ...prev,
            brandVoice: { ...prev.brandVoice!, traits: newTraits }
        }));
    };

    const removeTrait = (index: number) => {
        const newTraits = [...(strategy.brandVoice?.traits || [])];
        newTraits.splice(index, 1);
        const newStrategy = {
            ...strategy,
            brandVoice: { ...strategy.brandVoice!, traits: newTraits }
        };
        setStrategy(newStrategy);
        handleSave(newStrategy, true);
    };

    const addPillar = () => {
        setStrategy(prev => ({
            ...prev,
            contentPillars: [...(prev.contentPillars || []), '']
        }));
    };

    const updatePillar = (index: number, value: string) => {
        const newPillars = [...(strategy.contentPillars || [])];
        newPillars[index] = value;
        setStrategy(prev => ({ ...prev, contentPillars: newPillars }));
    };

    const removePillar = (index: number) => {
        const newPillars = [...(strategy.contentPillars || [])];
        newPillars.splice(index, 1);
        const newStrategy = { ...strategy, contentPillars: newPillars };
        setStrategy(newStrategy);
        handleSave(newStrategy, true);
    };

    const addStage = () => {
        setStrategy(prev => ({
            ...prev,
            journeyStages: [...(prev.journeyStages || []), { name: '', description: '' }]
        }));
    };

    const updateStage = (index: number, field: 'name' | 'description', value: string) => {
        const newStages = [...(strategy.journeyStages || [])];
        newStages[index] = { ...newStages[index], [field]: value };
        setStrategy(prev => ({ ...prev, journeyStages: newStages }));
    };

    const removeStage = (index: number) => {
        const newStages = [...(strategy.journeyStages || [])];
        newStages.splice(index, 1);
        const newStrategy = { ...strategy, journeyStages: newStages };
        setStrategy(newStrategy);
        handleSave(newStrategy, true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (

        <div className="min-h-screen bg-gray-50 py-12 relative">
            <div className={`transition-all duration-300 ${chatSection ? 'mr-96' : ''}`}>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* ... existing header ... */}
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Content Strategy</h1>
                            <p className="mt-1 text-sm text-gray-600">
                                Define your tenant-wide content strategy. This will guide the AI in generating content aligned with your brand.
                            </p>
                        </div>
                        {!isReadOnly && (
                            <div className="flex gap-2">

                            </div>
                        )}
                    </div>

                    {/* ... existing content ... */}

                    {isReadOnly && (
                        <div className="mb-8 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertTriangle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-yellow-700">
                                        You are viewing this page in read-only mode. Please contact your organization administrator to make changes to the Content Strategy.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-8">
                        {/* Target Audience Section */}
                        <div className="bg-white shadow sm:rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="flex items-center mb-4">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <Target className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <h3 className="ml-3 text-lg font-medium leading-6 text-gray-900">Target Audience</h3>
                                    {!isReadOnly && (
                                        <button
                                            onClick={() => setChatSection('Target Audience')}
                                            className="ml-auto flex items-center text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full transition-colors"
                                        >
                                            <Sparkles className="w-4 h-4 mr-1.5" />
                                            Chat with AI
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                    <div className="sm:col-span-6">
                                        <Label htmlFor="persona">
                                            Persona Description
                                        </Label>
                                        <div className="mt-1">
                                            <FormTextArea
                                                id="persona"
                                                rows={3}
                                                placeholder="e.g., Marketing Managers at B2B SaaS companies..."
                                                value={strategy.targetAudience?.persona || ''}
                                                onChange={(e) => setStrategy(prev => ({
                                                    ...prev,
                                                    targetAudience: { ...prev.targetAudience!, persona: e.target.value }
                                                }))}
                                                disabled={isReadOnly}
                                                onBlur={() => handleSave(undefined, true)}
                                            />
                                        </div>
                                    </div>

                                    <div className="sm:col-span-6">
                                        <Label>Pain Points</Label>
                                        <div className="mt-2 space-y-2">
                                            {(strategy.targetAudience?.painPoints || []).map((point, index) => (
                                                <div key={index} className="flex gap-2">
                                                    <FormInput
                                                        type="text"
                                                        value={point}
                                                        onChange={(e) => updatePainPoint(index, e.target.value)}
                                                        disabled={isReadOnly}
                                                        onBlur={() => handleSave(undefined, true)}
                                                    />
                                                    {!isReadOnly && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => removePainPoint(index)}
                                                            className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                            {!isReadOnly && (
                                                <Button variant="ghost" onClick={addPainPoint} className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-0 h-auto font-normal">
                                                    <Plus className="w-4 h-4 mr-1" /> Add Pain Point
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="sm:col-span-6">
                                        <Label htmlFor="goal">
                                            Audience Goal
                                        </Label>
                                        <div className="mt-1">
                                            <FormInput
                                                type="text"
                                                id="goal"
                                                value={strategy.targetAudience?.goal || ''}
                                                onChange={(e) => setStrategy(prev => ({
                                                    ...prev,
                                                    targetAudience: { ...prev.targetAudience!, goal: e.target.value }
                                                }))}
                                                disabled={isReadOnly}
                                                onBlur={() => handleSave(undefined, true)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Brand Voice Section */}
                        <div className="bg-white shadow sm:rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="flex items-center mb-4">
                                    <div className="p-2 bg-purple-100 rounded-lg">
                                        <Megaphone className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <h3 className="ml-3 text-lg font-medium leading-6 text-gray-900">Brand Voice</h3>
                                    {!isReadOnly && (
                                        <button
                                            onClick={() => setChatSection('Brand Voice')}
                                            className="ml-auto flex items-center text-sm text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-full transition-colors"
                                        >
                                            <Sparkles className="w-4 h-4 mr-1.5" />
                                            Chat with AI
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                    <div className="sm:col-span-3">
                                        <Label htmlFor="tone">
                                            Tone
                                        </Label>
                                        <div className="mt-1">
                                            <FormInput
                                                type="text"
                                                id="tone"
                                                placeholder="e.g., Professional, Friendly"
                                                value={strategy.brandVoice?.tone || ''}
                                                onChange={(e) => setStrategy(prev => ({
                                                    ...prev,
                                                    brandVoice: { ...prev.brandVoice!, tone: e.target.value }
                                                }))}
                                                disabled={isReadOnly}
                                                onBlur={() => handleSave(undefined, true)}
                                            />
                                        </div>
                                    </div>

                                    <div className="sm:col-span-3">
                                        <Label htmlFor="style">
                                            Style
                                        </Label>
                                        <div className="mt-1">
                                            <FormInput
                                                type="text"
                                                id="style"
                                                placeholder="e.g., Concise, Data-driven"
                                                value={strategy.brandVoice?.style || ''}
                                                onChange={(e) => setStrategy(prev => ({
                                                    ...prev,
                                                    brandVoice: { ...prev.brandVoice!, style: e.target.value }
                                                }))}
                                                disabled={isReadOnly}
                                                onBlur={() => handleSave(undefined, true)}
                                            />
                                        </div>
                                    </div>

                                    <div className="sm:col-span-6">
                                        <Label>Voice Traits</Label>
                                        <div className="mt-2 space-y-2">
                                            {(strategy.brandVoice?.traits || []).map((trait, index) => (
                                                <div key={index} className="flex gap-2">
                                                    <FormInput
                                                        type="text"
                                                        value={trait}
                                                        onChange={(e) => updateTrait(index, e.target.value)}
                                                        disabled={isReadOnly}
                                                        onBlur={() => handleSave(undefined, true)}
                                                    />
                                                    {!isReadOnly && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => removeTrait(index)}
                                                            className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                            {!isReadOnly && (
                                                <Button variant="ghost" onClick={addTrait} className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 p-0 h-auto font-normal">
                                                    <Plus className="w-4 h-4 mr-1" /> Add Trait
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Content Pillars Section */}
                        <div className="bg-white shadow sm:rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="flex items-center mb-4">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                        <Layers className="w-6 h-6 text-green-600" />
                                    </div>
                                    <h3 className="ml-3 text-lg font-medium leading-6 text-gray-900">Content Pillars</h3>
                                    {!isReadOnly && (
                                        <button
                                            onClick={() => setChatSection('Content Pillars')}
                                            className="ml-auto flex items-center text-sm text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-full transition-colors"
                                        >
                                            <Sparkles className="w-4 h-4 mr-1.5" />
                                            Chat with AI
                                        </button>
                                    )}
                                </div>
                                <div className="space-y-4">
                                    {(strategy.contentPillars || []).map((pillar, index) => (
                                        <div key={index} className="flex gap-2">
                                            <FormInput
                                                type="text"
                                                placeholder="e.g., AI Technology Trends"
                                                value={pillar}
                                                onChange={(e) => updatePillar(index, e.target.value)}
                                                disabled={isReadOnly}
                                                onBlur={() => handleSave(undefined, true)}
                                            />
                                            {!isReadOnly && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => removePillar(index)}
                                                    className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    {!isReadOnly && (
                                        <Button variant="ghost" onClick={addPillar} className="text-green-600 hover:text-green-800 hover:bg-green-50 p-0 h-auto font-normal">
                                            <Plus className="w-4 h-4 mr-1" /> Add Pillar
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Journey Stages Section */}
                        <div className="bg-white shadow sm:rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="flex items-center mb-4">
                                    <div className="p-2 bg-orange-100 rounded-lg">
                                        <Map className="w-6 h-6 text-orange-600" />
                                    </div>
                                    <h3 className="ml-3 text-lg font-medium leading-6 text-gray-900">Customer Journey Stages</h3>
                                    {!isReadOnly && (
                                        <button
                                            onClick={() => setChatSection('Customer Journey Stages')}
                                            className="ml-auto flex items-center text-sm text-orange-600 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 px-3 py-1.5 rounded-full transition-colors"
                                        >
                                            <Sparkles className="w-4 h-4 mr-1.5" />
                                            Chat with AI
                                        </button>
                                    )}
                                </div>
                                <div className="space-y-6">
                                    {(strategy.journeyStages || []).map((stage, index) => (
                                        <div key={index} className="flex gap-4 items-start p-4 bg-gray-50 rounded-lg relative group">
                                            <div className="flex-1 space-y-4">
                                                <div>
                                                    <Label className="text-xs font-medium text-gray-500 uppercase">Stage Name</Label>
                                                    <div className="mt-1">
                                                        <FormInput
                                                            type="text"
                                                            value={stage.name}
                                                            onChange={(e) => updateStage(index, 'name', e.target.value)}
                                                            disabled={isReadOnly}
                                                            onBlur={() => handleSave(undefined, true)}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label className="text-xs font-medium text-gray-500 uppercase">Description</Label>
                                                    <div className="mt-1">
                                                        <FormTextArea
                                                            rows={2}
                                                            value={stage.description}
                                                            onChange={(e) => updateStage(index, 'description', e.target.value)}
                                                            disabled={isReadOnly}
                                                            onBlur={() => handleSave(undefined, true)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {!isReadOnly && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => removeStage(index)}
                                                    className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    {!isReadOnly && (
                                        <Button variant="ghost" onClick={addStage} className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-0 h-auto font-normal">
                                            <Plus className="w-4 h-4 mr-1" /> Add Stage
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Chat Panel */}
            {chatSection && (
                <ContentStrategyAIChat
                    onClose={() => setChatSection(null)}
                    onUpdateStrategy={() => {
                        loadSettings(true); // Reload settings in background preventing UI remount
                        toast.success('Strategy updated by AI');
                    }}
                    currentStrategy={strategy}
                    initialContext={chatSection}
                />
            )}
        </div >

    );
};

export default ContentStrategyPage;

